import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from '../utils/axios';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // ...existing code...

  const [token, setToken] = useState(() => localStorage.getItem('token')); // Initialize from localStorage
  const [isInitialized, setIsInitialized] = useState(false);
  const [userData, setUserData] = useState(null);

  // Query to get current user
  const { data: user, isLoading, refetch } = useQuery(
    'currentUser',
    async () => {
      if (!token) return null;
      const response = await axios.get('/auth/me');
      return response.data;
    },
    {
      // Must not gate on !userData: login() sets userData immediately but the payload
      // omits fields (e.g. phone) that /auth/me returns; otherwise /me never runs.
      enabled: !!token,
      retry: false,
      onError: () => {
        // If token is invalid, remove it
        logout();
      },
      onSuccess: (data) => {
        setUserData(data);
        setIsInitialized(true);
      },
      onSettled: () => {
        if (!token) {
          setIsInitialized(true);
        }
      }
    }
  );

  useEffect(() => {
    // Initialize auth state on app startup
    if (token && !userData) {
      // Token exists but no user data, trigger refetch
      refetch();
    } else if (!token) {
      setIsInitialized(true);
      setUserData(null);
    }
  }, [token, userData, refetch]);

  const login = (newToken, userDataFromLogin) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUserData(userDataFromLogin);
    setIsInitialized(true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUserData(null);
    // Redirect to appropriate login page based on current location
    const isOnAdminPage = window.location.pathname.startsWith('/admin');
    if (isOnAdminPage) {
      window.location.href = '/admin/login';
    } else {
      window.location.href = '/login';
    }
  };

  const value = {
    user: userData || user,
    token,
    isLoading: !isInitialized || (isLoading && !userData),
    login,
    logout,
    refetch
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
